// React.lazy with a safety net for stale deployments.
//
// The app is split into files that load on demand (the CMS, the public pages). Each deploy gives those files new
// names, so a browser tab that was opened BEFORE a deploy asks for a file that no longer exists and the import fails.
// Without a net that is a blank screen. Here the first failure reloads the page once (it then gets the new build); a
// second failure in the same tab is a real problem and is thrown so an error boundary can say so.
import { lazy } from "react";

const KEY = "mw-chunk-reload";

// sessionStorage that never throws (private windows, blocked storage). `ok` is false when it cannot be used.
export function safeSession(win = typeof window !== "undefined" ? window : undefined) {
  try {
    const s = win && win.sessionStorage;
    if (!s) throw new Error("none");
    s.setItem("mw-probe", "1"); s.removeItem("mw-probe");
    return { ok: true, get: k => s.getItem(k), set: (k, v) => s.setItem(k, v), remove: k => s.removeItem(k) };
  } catch {
    return { ok: false, get: () => null, set: () => {}, remove: () => {} };
  }
}

// -> Promise<module>. `factory` is () => import("./X"). Exported (not only wrapped in lazy) so it can be tested.
export async function loadWithRetry(factory, { storage = safeSession(), reload = () => window.location.reload() } = {}) {
  try {
    const mod = await factory();
    storage.remove(KEY);
    return mod;
  } catch (err) {
    // Reload at most once per tab, and only if we can REMEMBER that we did: without storage a reload could loop forever.
    if (storage.ok && !storage.get(KEY)) {
      storage.set(KEY, "1");
      reload();
      return new Promise(() => {});    // never settles: the page is being replaced
    }
    throw err;
  }
}

export const lazyWithRetry = (factory, options) => lazy(() => loadWithRetry(factory, options));
